import { create } from "zustand";

import type { OpsAlert } from "../api/types";
import { fresher } from "../lib/opscard";

/**
 * Операционные тревоги: живые и недавно снятые.
 *
 * # Почему замещение по id
 *
 * Тревога и её снятие приезжают ОДНИМ типом сообщения с ОДНИМ идентификатором.
 * Отдельные типы «поднялась» и «снялась» заставили бы склеивать их руками, то
 * есть держать порядок доставки, которого у сокета нет. Та же идиома, что у
 * карточки замка и сигнала кита.
 *
 * # Потолок
 *
 * Событий десятки в сутки: правила подтверждаются, дедуп даёт одно сообщение на
 * переход, а крикун глушится автоматически. Потолок защищает не от потока, а от
 * вкладки, открытой сутками.
 */
const OPS_LIMIT = 100;

interface OpsAlertsState {
  /** Ключ — id события. Порядок показа задаёт сортировка, а не вставка. */
  alerts: Record<string, OpsAlert>;
  /** Одно сообщение сокета либо строка ответа REST. */
  ingest: (alert: OpsAlert) => void;
  /** Пачка из REST: поднятие висящих тревог при открытии вкладки. */
  ingestMany: (alerts: OpsAlert[]) => void;
  /** Убрать карточку с экрана (снятые гаснут по TTL). */
  drop: (id: string) => void;
  clear: () => void;
}

/** prune выбрасывает самые давние по времени события. */
function prune(next: Record<string, OpsAlert>): Record<string, OpsAlert> {
  const ids = Object.keys(next);
  if (ids.length <= OPS_LIMIT) return next;
  const sorted = ids.sort((a, b) => Date.parse(next[b]?.at ?? "") - Date.parse(next[a]?.at ?? ""));
  for (const id of sorted.slice(OPS_LIMIT)) delete next[id];
  return next;
}

export const useOpsAlerts = create<OpsAlertsState>((setState) => ({
  alerts: {},

  ingest: (alert) => {
    if (!alert?.id) return;
    setState((state) => ({ alerts: prune({ ...state.alerts, [alert.id]: alert }) }));
  },

  ingestMany: (alerts) => {
    if (!alerts?.length) return;
    setState((state) => {
      const next = { ...state.alerts };
      for (const a of alerts) {
        // Только не старее показанного: сокет уже мог снять тревогу, пока
        // REST-ответ лежал в сети, и откат «снято» обратно в «тревога» был бы
        // ложным звонком. Почему именно так — у fresher.
        if (a?.id && fresher(next[a.id], a)) next[a.id] = a;
      }
      return { alerts: prune(next) };
    });
  },

  drop: (id) =>
    setState((state) => {
      if (!(id in state.alerts)) return state;
      const next = { ...state.alerts };
      delete next[id];
      return { alerts: next };
    }),

  clear: () => setState({ alerts: {} }),
}));
